import { useEffect, useState, useCallback, useMemo, lazy, Suspense } from 'react';
import './styles/global.css';
import { MapCanvas } from './canvas/MapCanvas';
import { IsoMapCanvas } from './canvas/IsoMapCanvas';
import { isLegacyIso } from './canvas/three/legacyIso';
import { VerticalToolbar } from './components/VerticalToolbar';
import { useCampaignMapStore } from './stores/campaignMapStore';
import { ChatWindow } from './components/ChatWindow';
import { DiceCapture } from './components/DiceCapture';

// Lazy-loaded heavy components — DiceRoller pulls in Three.js (~688KB),
// GMTray is 3400 lines + 9 sub-panels (GM-only tools)
const DiceRoller = lazy(() => import('./components/DiceRoller').then(m => ({ default: m.DiceRoller })));
const GMTray = lazy(() => import('./components/dm-console/GMTray').then(m => ({ default: m.GMTray })));
// The 3D battle board — three.js r186 stays out of the main bundle.
const ThreeMapCanvas = lazy(() => import('./canvas/three/ThreeMapCanvas').then(m => ({ default: m.ThreeMapCanvas })));
import { useIsGM } from './hooks/useIsGM';
import { PartyPanel } from './components/PartyPanel';
import { MobPanel } from './components/dm-console/MobPanel';
import { InitiativePanel } from './components/InitiativePanel';
import { ThemePicker } from './components/ThemePicker';
import { WindowManager } from './components/WindowManager';
import { useDiceStore } from './stores/diceStore';
import { useDMConsoleStore } from './stores/dmConsoleStore';
import { useMobStore } from './stores/mobStore';
import { useMapStore } from './stores/mapStore';
import { useTokenStore } from './stores/tokenStore';
import { useAuthStore, getUserDisplayName } from './stores/authStore';
import { useCampaignStore } from './stores/campaignStore';
import { useCharacterStore } from './stores/characterStore';
import { useTokenImageStore } from './stores/tokenImageStore';
import { GouldCorner, SectionHeader, MachinaCenterpiece, BladeIcon } from './components/Ornaments';
import { useSupabaseSync } from './hooks/useSupabaseSync';
import { ProfilePage } from './components/profile/ProfilePage';
import { TownCrier } from './components/TownCrier';
import { UserNotice } from './components/UserNotice';
import { CampaignLobby } from './components/CampaignLobby';
import { WorldsPage, WORLD_INTENT_KEY, WORLD_INTENT_CREATE } from './components/worlds/WorldsPage';
import { ENTER_CAMPAIGN_KEY } from './components/worlds/PublicWorldsPage';
import { PLAYSETS, PlaysetId } from '@machina/core';
import { JoinPage } from './components/join/JoinPage';
import { DinoRaceView } from './components/DinoRaceView';
import { OpenTasksPanel } from './components/onboarding/OpenTasksPanel';
import { Icon } from './components/Icon';
import { PsycheThoughtDisplay } from './components/player/PsycheThoughtDisplay';
import { NpcBarkOverlay } from './components/NpcBarkOverlay';
import { NotificationOverlay } from './components/NotificationOverlay';
import { PlayerCardDrawer } from './components/PlayerCardDrawer';
import { ConnectionIndicator } from './components/ConnectionIndicator';
import { BugReportButton } from './components/BugReportButton';
import { PlayerSoundToggle } from './components/PlayerSoundToggle';
import { CharacterSheetPage } from './components/CharacterSheetPage';
import { GMAssistantPage } from './components/GMAssistantPage';
import { useNpcHealthMonitor } from './hooks/useNpcHealthMonitor';
import { FeatureTour } from './components/tour/FeatureTour';
import { useFeatureTourStore } from './stores/featureTourStore';
import { trpc } from './lib/trpc';
import { useInitiativeStore } from './stores/initiativeStore';
import { useCombatStore } from './stores/combatStore';
import { useEncounterStore } from './stores/encounterStore';
import { useSoloGamemasterStore } from './stores/soloGamemasterStore';
import { useChatStore } from './stores/chatStore';
import { useBestiaryStore } from './stores/bestiaryStore';
import { useIsoSpriteStore } from './stores/isoSpriteStore';
import { createFallbackTokenImage } from './lib/tokenFallbacks';
import { activateCampaignSystem, systemHasCombat } from './systems';
// useTierStore imported in useSupabaseSync for tier loading
// Feature flags used in CampaignWizard for campaign creation gating

// Expose stores for E2E testing (dev only)
if (import.meta.env.DEV) {
  (window as any).__E2E__ = {
    initiativeStore: useInitiativeStore,
    characterStore: useCharacterStore,
    mobStore: useMobStore,
    tokenStore: useTokenStore,
    combatStore: useCombatStore,
    encounterStore: useEncounterStore,
    campaignStore: useCampaignStore,
    chatStore: useChatStore,
    bestiaryStore: useBestiaryStore,
    mapStore: useMapStore,
    campaignMapStore: useCampaignMapStore,
    isoSpriteStore: useIsoSpriteStore,
  };
}

// Parse join code from hash route (e.g., #join/ABC12345)
function parseJoinCode(hash: string): string | null {
  const match = hash.match(/^#join\/([A-Za-z0-9]+)$/);
  return match ? match[1].toUpperCase() : null;
}

function App() {
  const setRollFn = useDiceStore((state) => state.setRollFn);
  const { isOpen: dmConsoleOpen, togglePanel: toggleDMConsole } = useDMConsoleStore();
  const { user, signOut } = useAuthStore();
  const { campaigns, currentCampaignId, setCurrentCampaign, loadCampaigns } = useCampaignStore();
  const [route, setRoute] = useState(window.location.hash);
  const [showProfile, setShowProfile] = useState(false);
  const [showTownCrier, setShowTownCrier] = useState(false);
  const [inGame, setInGame] = useState(false);
  const [showDinoRace, setShowDinoRace] = useState(false);
  // Pending world intent handed off from the public (pre-login) Worlds front door.
  // Once the user authenticates we route to `#worlds` and pass this to WorldsPage,
  // which opens CampaignWizard prefilled (or fresh). See WORLD_INTENT_KEY.
  const [worldIntent, setWorldIntent] = useState<PlaysetId | 'fresh' | null>(null);

  const currentCampaign = campaigns.find((c) => c.id === currentCampaignId);

  /**
   * Activate the campaign's game system whenever the current campaign changes.
   *
   * `handleEnterCampaign` already does this on the lobby path, but it is not the
   * only door: entering from the public Worlds hand-off and joining by invite
   * link both call `setCurrentCampaign` directly, and neither activated
   * anything. A player arriving through either one was rolling under whichever
   * system the previous campaign left active — or, on a fresh page load, none.
   *
   * Keying the activation to the campaign rather than to the door removes the
   * whole class of bug: a new entry path cannot forget to do this.
   */
  const [hasCombat, setHasCombat] = useState(true);
  useEffect(() => {
    if (!currentCampaignId) return;
    activateCampaignSystem(currentCampaign?.settings?.systemId);
    setHasCombat(systemHasCombat());
  }, [currentCampaignId, currentCampaign?.settings?.systemId]);
  const isGM = useIsGM();
  const mapMode = useCampaignMapStore((s) => s.mapMode);

  // Parse join code from route
  const joinCode = parseJoinCode(route);

  // Get display name from auth
  const displayName = getUserDisplayName(user);

  // Initialize Supabase sync for multiplayer
  const { userName, isSceneReady } = useSupabaseSync();

  // NPC health monitor — auto-heals party members outside combat
  useNpcHealthMonitor();

  useEffect(() => {
    const handleHashChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Listen for dice roll requests from popup character sheet windows
  useEffect(() => {
    const channel = new BroadcastChannel('machina-dice');
    let busy = false;

    channel.onmessage = async (event) => {
      if (event.data?.type === 'DICE_ROLL_REQUEST') {
        // Drop messages while a roll is in progress or draining
        if (busy) return;
        busy = true;

        try {
          const { requestId, notation } = event.data;
          const result = await useDiceStore.getState().roll(notation);
          if (result) {
            channel.postMessage({ type: 'DICE_ROLL_RESULT', requestId, result });
          }
        } finally {
          // Keep the gate closed for 500ms after roll completes so any
          // queued BroadcastChannel messages get silently drained
          setTimeout(() => { busy = false; }, 500);
        }
      }
    };

    return () => channel.close();
  }, []);

  // Load campaigns on mount
  useEffect(() => {
    if (user?.id) {
      loadCampaigns(user.id);
    }
  }, [user?.id, loadCampaigns]);

  // Token image store (handles localStorage persistence)
  const { tokenImages: mobTokenImages, loadFromCache, setTokenImage, getMissingTokens } = useTokenImageStore();

  // Load cached tokens on mount
  useEffect(() => {
    loadFromCache();
  }, [loadFromCache]);

  // Encounter presets for post-race battles (defined before useEffect that uses it)
  // Keys must match scenario.type from dino-race.html: undead, pirates, dinosaur, yuan_ti, cultists
  const encounterPresets = useMemo(() => ({
    undead: [
      { name: 'Zombie', ac: 8, maxHp: 22, str: 13, dex: 6, con: 16, int: 3, wis: 6, cha: 5 },
      { name: 'Zombie', ac: 8, maxHp: 22, str: 13, dex: 6, con: 16, int: 3, wis: 6, cha: 5 },
      { name: 'Zombie', ac: 8, maxHp: 22, str: 13, dex: 6, con: 16, int: 3, wis: 6, cha: 5 },
      { name: 'Ghoul', ac: 12, maxHp: 22, str: 13, dex: 15, con: 10, int: 7, wis: 10, cha: 6 },
    ],
    pirates: [
      { name: 'Pirate', ac: 12, maxHp: 11, str: 11, dex: 12, con: 12, int: 10, wis: 10, cha: 10 },
      { name: 'Pirate', ac: 12, maxHp: 11, str: 11, dex: 12, con: 12, int: 10, wis: 10, cha: 10 },
      { name: 'Pirate Captain', ac: 15, maxHp: 32, str: 14, dex: 16, con: 14, int: 11, wis: 12, cha: 14 },
    ],
    dinosaur: [
      { name: 'Allosaurus', ac: 13, maxHp: 51, str: 19, dex: 13, con: 17, int: 2, wis: 12, cha: 5 },
      { name: 'Velociraptor', ac: 13, maxHp: 10, str: 6, dex: 14, con: 13, int: 4, wis: 12, cha: 6 },
      { name: 'Velociraptor', ac: 13, maxHp: 10, str: 6, dex: 14, con: 13, int: 4, wis: 12, cha: 6 },
    ],
    yuan_ti: [
      { name: 'Yuan-ti Pureblood', ac: 11, maxHp: 40, str: 11, dex: 12, con: 12, int: 13, wis: 12, cha: 14 },
      { name: 'Yuan-ti Pureblood', ac: 11, maxHp: 40, str: 11, dex: 12, con: 12, int: 13, wis: 12, cha: 14 },
      { name: 'Yuan-ti Malison', ac: 12, maxHp: 66, str: 16, dex: 14, con: 13, int: 14, wis: 12, cha: 16 },
    ],
    cultists: [
      { name: 'Cultist', ac: 12, maxHp: 9, str: 11, dex: 12, con: 10, int: 10, wis: 11, cha: 10 },
      { name: 'Cultist', ac: 12, maxHp: 9, str: 11, dex: 12, con: 10, int: 10, wis: 11, cha: 10 },
      { name: 'Cult Fanatic', ac: 13, maxHp: 33, str: 11, dex: 14, con: 12, int: 10, wis: 13, cha: 14 },
    ],
  }), []);

  const removeAllMobs = useMobStore((s) => s.removeAllMobs);
  const setActiveMap = useMapStore((s) => s.setActiveMap);
  const setZoom = useMapStore((s) => s.setZoom);
  const removeToken = useTokenStore((s) => s.removeToken);

  // Generate and load racetrack battle map + mob tokens when dino race opens
  useEffect(() => {
    if (!showDinoRace) return;

    // Generate the battle map
    const generateRacetrackMap = async () => {
      console.log('[App] Generating racetrack battle map...');
      try {
        const data = await trpc.maps.generate.mutate({
          prompt: {
            userPrompt: 'Battle map for dinosaur racing track encounter',
            environment: 'racetrack',
            style: 'top_down_tactical',
            mood: 'bright_day',
            widthFeet: 80,
            heightFeet: 60,
            features: ['dinosaur racing track', 'wooden grandstands', 'tropical plants', 'excited crowd', 'sandy track'],
            avoid: ['characters', 'creatures', 'monsters', 'people'],
          },
        });
        console.log('[App] Racetrack map generated!');

        // Create map texture and set as active
        const mapTexture = {
          id: crypto.randomUUID(),
          colorUrl: data.imageUrl,
          bounds: { width: 80, height: 60, originX: 0, originY: 0 },
          pixelWidth: 800,
          pixelHeight: 600,
          pixelsPerFoot: 10,
          zIndex: 0,
          opacity: 1,
        };

        useMapStore.getState().setActiveMap({
          id: crypto.randomUUID(),
          name: 'Racetrack Battle',
          bounds: { width: 80, height: 60, originX: 0, originY: 0 },
          gridType: 'square',
          gridSizeFeet: 5,
          baseTexture: mapTexture,
          textureLayers: [],
          materialZones: [],
          environment: 'racetrack',
          mood: 'bright_day',
          style: 'top_down_tactical',
          generationHistory: [],
          walls: [],
          doors: [],
          tags: ['dino-race', 'encounter'],
          createdAt: new Date(),
          updatedAt: new Date(),
        });

        // Set zoom for better visibility
        useMapStore.getState().setZoom(1.5);
      } catch (error) {
        console.error('[App] Failed to generate racetrack map:', error);
      }
    };

    // Generate tokens for all unique mob types (skip cached ones)
    const generateMobTokens = async () => {
      // Collect unique mob names from all encounter presets
      const uniqueMobs = new Set<string>();
      Object.values(encounterPresets).forEach(mobs => {
        mobs.forEach(mob => uniqueMobs.add(mob.name));
      });

      // Check which tokens are already cached using the store method
      const missingMobs = getMissingTokens(Array.from(uniqueMobs));

      if (missingMobs.length === 0) {
        console.log('[App] All tokens already cached, skipping generation');
        return;
      }

      console.log('[App] Missing tokens to generate:', missingMobs);

      // Helper to generate with retry
      const generateWithRetry = async (mobName: string, maxRetries = 2): Promise<string | null> => {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              console.log(`[App] Retry ${attempt} for ${mobName}...`);
              await new Promise(r => setTimeout(r, 1000));
            }

            const result = await trpc.maps.generateToken.mutate({ creatureName: mobName });
            return result.imageUrl;
          } catch (error) {
            console.error(`[App] Attempt ${attempt + 1} failed for ${mobName}:`, error);
          }
        }
        return null;
      };

      // Generate only missing tokens
      let generatedCount = 0;
      for (const mobName of missingMobs) {
        const imageUrl = await generateWithRetry(mobName);
        if (imageUrl) {
          setTokenImage(mobName, imageUrl);
          generatedCount++;
          console.log(`[App] Generated token for ${mobName}`);
        } else {
          const fallbackUrl = createFallbackTokenImage(mobName, 'monster');
          setTokenImage(mobName, fallbackUrl);
          console.error(`[App] Failed to generate token for ${mobName} after retries`);
        }
        // Small delay between requests to avoid rate limits
        await new Promise(r => setTimeout(r, 500));
      }

      console.log('[App] Token generation complete! Generated', generatedCount, 'new tokens');
    };

    // Run both in parallel
    generateRacetrackMap();
    generateMobTokens();
  }, [showDinoRace, encounterPresets, getMissingTokens, setTokenImage]);

  // Listen for battle start messages from dino race iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'START_BATTLE') {
        const scenario = event.data.scenario;
        const battleMap = event.data.battleMap;
        console.log('[App] Battle started from dino race:', scenario);

        // Close the dino race and return to battle map
        setShowDinoRace(false);

        // Load the battle map if provided
        if (battleMap?.imageUrl) {
          console.log('[App] Loading battle map from dino race');
          const mapTexture = {
            id: crypto.randomUUID(),
            colorUrl: battleMap.imageUrl,
            bounds: {
              width: battleMap.widthFeet || 80,
              height: battleMap.heightFeet || 60,
              originX: 0,
              originY: 0,
            },
            pixelWidth: (battleMap.widthFeet || 80) * 10,
            pixelHeight: (battleMap.heightFeet || 60) * 10,
            pixelsPerFoot: 10,
            zIndex: 0,
            opacity: 1,
          };

          setActiveMap({
            id: crypto.randomUUID(),
            name: 'Racetrack Battle',
            bounds: {
              width: battleMap.widthFeet || 80,
              height: battleMap.heightFeet || 60,
              originX: 0,
              originY: 0,
            },
            gridType: 'square',
            gridSizeFeet: 5,
            baseTexture: mapTexture,
            textureLayers: [],
            materialZones: [],
            environment: 'racetrack',
            mood: 'bright_day',
            style: 'top_down_tactical',
            generationHistory: [],
            walls: [],
            doors: [],
            tags: ['dino-race', 'encounter'],
            createdAt: new Date(),
            updatedAt: new Date(),
          });

          // Zoom in for better visibility during battle
          setZoom(1.5);
        }

        // Clear existing mobs and their tokens
        await removeAllMobs();

        // Remove any existing mob tokens (non-player tokens)
        const currentTokens = useTokenStore.getState().tokens;
        for (const [id, token] of currentTokens) {
          if (!token.isPlayerControlled) {
            removeToken(id);
          }
        }

        const encounterType = scenario?.type || 'undead';
        const mobs = encounterPresets[encounterType as keyof typeof encounterPresets] || encounterPresets.undead;

        // Map dimensions for positioning (in feet, converted to pixels at 10px per foot)
        const mapWidthFeet = battleMap?.widthFeet || 80;
        const mapHeightFeet = battleMap?.heightFeet || 60;
        const pixelsPerFoot = 10;

        // Position mobs on the right side of the map, spread vertically
        const startX = mapWidthFeet * 0.7 * pixelsPerFoot; // 70% across the map
        const mobSpacingY = (mapHeightFeet * pixelsPerFoot) / (mobs.length + 1);

        // Spawn the mobs with tokens via unified pipeline
        const mobStore = useMobStore.getState();
        for (let index = 0; index < mobs.length; index++) {
          const mobData = mobs[index];
          const tokenY = mobSpacingY * (index + 1);
          const pregenImage = mobTokenImages[mobData.name];
          await mobStore.spawnMob({
            campaignId: currentCampaignId || '',
            name: mobData.name,
            ac: mobData.ac,
            maxHp: mobData.maxHp,
            abilities: { str: mobData.str, dex: mobData.dex, con: mobData.con, int: mobData.int, wis: mobData.wis, cha: mobData.cha },
          }, {
            position: { x: startX, y: tokenY },
            rollInitiative: false,
            imageUrl: pregenImage,
          });
        }

        console.log(`[App] Spawned ${mobs.length} mobs with tokens for ${encounterType} encounter`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [encounterPresets, removeAllMobs, setActiveMap, setZoom, removeToken, mobTokenImages, currentCampaignId]);

  // Consume pending intents handed off from the public (pre-login) Worlds front
  // door once the user is authenticated and back in the app shell. Mirrors the
  // OAuth pending-beta-key consumption in authStore. Runs while in the lobby
  // (!inGame) so it never fights an active game session.
  useEffect(() => {
    if (!user?.id || inGame) return;

    // A campaign created from the public page (logged-in visitor) → enter it.
    const enterId = sessionStorage.getItem(ENTER_CAMPAIGN_KEY);
    if (enterId) {
      sessionStorage.removeItem(ENTER_CAMPAIGN_KEY);
      setCurrentCampaign(enterId);
      setInGame(true);
      return;
    }

    // A world the visitor picked before signing up → open the wizard prefilled.
    const intent = sessionStorage.getItem(WORLD_INTENT_KEY);
    if (intent) {
      sessionStorage.removeItem(WORLD_INTENT_KEY);
      const resolved: PlaysetId | 'fresh' =
        intent === WORLD_INTENT_CREATE
          ? 'fresh'
          : intent in PLAYSETS
            ? (intent as PlaysetId)
            : 'fresh';
      setWorldIntent(resolved);
      if (window.location.hash !== '#worlds') {
        window.location.hash = '#worlds';
        setRoute('#worlds');
      }
    }
  }, [user?.id, inGame, setCurrentCampaign]);

  // Clear join route and go to lobby
  const handleBackFromJoin = useCallback(() => {
    window.location.hash = '';
    setRoute('');
  }, []);

  // Handle successful join - enter the campaign
  const handleJoinedCampaign = useCallback((campaignId: string) => {
    window.location.hash = '';
    setRoute('');
    setCurrentCampaign(campaignId);
    setInGame(true);
  }, [setCurrentCampaign]);

  // Character sheet popup window route
  const sheetMatch = route.match(/^#sheet\/([^/]+)\/([^/]+)$/);
  if (sheetMatch) {
    return <CharacterSheetPage campaignId={sheetMatch[1]} characterId={sheetMatch[2]} />;
  }

  // GM Assistant popup window route
  const gmMatch = route.match(/^#gm\/([^/]+)$/);
  if (gmMatch) {
    return <GMAssistantPage campaignId={gmMatch[1]} />;
  }

  // Dev utility: dice capture tool
  if (route === '#capture') {
    return <DiceCapture />;
  }

  // Join route - show JoinPage
  if (joinCode) {
    return (
      <JoinPage
        code={joinCode}
        onBack={handleBackFromJoin}
        onJoined={handleJoinedCampaign}
        onLogin={handleBackFromJoin}
        onSignup={handleBackFromJoin}
      />
    );
  }

  // Handle entering a campaign from lobby
  const handleEnterCampaign = (campaignId: string) => {
    // Clear stale initiative data from previous campaign
    useInitiativeStore.getState().clearAllEntries();

    // Activate AI DM mode for solo and ai_dm campaigns.
    // Read FRESH store state, not the render-scope `campaigns` array: right
    // after SoloCampaignSetup creates a campaign, onCreated fires before App
    // re-renders, so the closure array doesn't contain the new campaign yet.
    // The stale lookup returned undefined → setSoloMode(null) → the AI DM
    // never initialized on every freshly created solo campaign.
    const campaign = useCampaignStore.getState().campaigns.find((c) => c.id === campaignId);

    // Activate the campaign's game system before anything can roll. Unset means
    // 5e; an unregistered id also means 5e, with a warning, rather than leaving
    // the previous campaign's rules quietly in force.
    activateCampaignSystem(campaign?.settings?.systemId);

    const soloStore = useSoloGamemasterStore.getState();
    const hasAiDm = campaign?.campaignType === 'solo' || campaign?.campaignType === 'ai_dm';
    if (hasAiDm) {
      // AI DM campaigns: all players get AI features (gate is on campaign creation, not joining)
      soloStore.setSoloMode(campaignId);
      // Solo init happens in useSupabaseSync after all data loads complete
    } else {
      soloStore.setSoloMode(null);
    }

    setCurrentCampaign(campaignId);
    setInGame(true);
  };

  // Handle leaving campaign back to lobby
  const handleLeaveCampaign = () => {
    setInGame(false);
  };

  // Worlds gallery route — in-app (logged-in) entry. The SAME page also serves as
  // the public pre-login front door via main.tsx's `#worlds` branch (outside
  // AuthGuard); this branch only runs for an authenticated user inside App.
  // `autoOpenIntent` opens the wizard prefilled when arriving via a signup handoff.
  if (!inGame && route === '#worlds') {
    return (
      <WorldsPage
        onEnterCampaign={handleEnterCampaign}
        autoOpenIntent={worldIntent ?? undefined}
        onIntentConsumed={() => setWorldIntent(null)}
        onBack={() => {
          setWorldIntent(null);
          window.location.hash = '';
          setRoute('');
        }}
      />
    );
  }

  // Show lobby if not in game
  if (!inGame) {
    return <CampaignLobby onEnterCampaign={handleEnterCampaign} />;
  }

  // Loading screen while scene initializes
  if (!isSceneReady) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a080c',
        color: '#d4af37',
        fontFamily: 'Georgia, serif',
        gap: '1.5rem',
      }}>
        <MachinaCenterpiece size={320} style={{ opacity: 0.9, animation: 'loaderFadeIn 0.6s ease-out' }} />
        <div style={{
          fontSize: '1.1rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          opacity: 0.8,
          animation: 'loaderFadeIn 0.8s ease-out',
        }}>
          {currentCampaign?.name || 'Entering the realm'}...
        </div>
        <div style={{
          width: '240px',
          height: '2px',
          borderRadius: '1px',
          background: 'linear-gradient(90deg, transparent 0%, #d4af37 50%, transparent 100%)',
          animation: 'loaderSweep 1.8s ease-in-out infinite, loaderFadeIn 1s ease-out',
        }} />
        <style>{`
          @keyframes loaderSweep {
            0% { opacity: 0.2; transform: scaleX(0.3); }
            50% { opacity: 1; transform: scaleX(1); }
            100% { opacity: 0.2; transform: scaleX(0.3); }
          }
          @keyframes loaderFadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // Game view
  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
        {/* Header - Gould-inspired ornate design */}
        <header style={{
          padding: 'var(--spacing-sm) var(--spacing-lg)',
          backgroundColor: 'var(--color-surface)',
          borderBottom: `2px solid var(--color-accent)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          boxShadow: 'inset 0 -10px 20px rgba(0,0,0,0.2)'
        }}>
          {/* Corner ornaments */}
          <GouldCorner
            size={24}
            style={{ position: 'absolute', top: 4, left: 4 }}
          />
          <GouldCorner
            size={24}
            style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)' }}
          />

          {/* Beta stamp - old timey ink stamp look */}
          <div style={{
            position: 'absolute',
            top: 6,
            right: 32,
            background: 'transparent',
            color: '#8b1a1a',
            padding: '3px 10px',
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            transform: 'rotate(8deg)',
            border: '3px solid #8b1a1a',
            borderRadius: '4px',
            fontFamily: 'var(--font-display)',
            zIndex: 10,
            opacity: 0.85,
            // Ink stamp texture effect
            textShadow: '0 0 1px #8b1a1a',
            boxShadow: `
              inset 0 0 0 1px rgba(139, 26, 26, 0.3),
              0 0 0 1px rgba(139, 26, 26, 0.1)
            `,
            // Slight imperfection via filter
            filter: 'saturate(0.9) contrast(1.1)',
          }}>
            BETA
          </div>

          {/* Left: Back button + Campaign name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            {/* Back to Lobby */}
            <button
              onClick={handleLeaveCampaign}
              title="Back to Campaign Lobby"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
              }}
            >
              <Icon name="chevron-left" size={14} />
              Lobby
            </button>

            {/* Campaign title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <BladeIcon size={28} />
              <div>
                <h1 style={{
                  fontSize: 'var(--font-size-lg)',
                  color: 'var(--color-accent)',
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                }}>
                  {currentCampaign?.name || 'Machina'}
                </h1>
              </div>
            </div>
          </div>

          {/* Center decoration - Machina branding */}
          <MachinaCenterpiece size={260} style={{ opacity: 0.85 }} />

          <div style={{
            display: 'flex',
            gap: 'var(--spacing-md)',
            alignItems: 'center',
            fontSize: 'var(--font-size-sm)'
          }}>
            {/* Town Crier button - Scroll with wooden spools that unrolls */}
            <div
              onClick={() => setShowTownCrier(true)}
              title="What's New in Machina"
              className="scroll-button"
              style={{
                width: 36,
                height: 46,
                padding: 0,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transform: 'rotate(-12deg)',
                filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.5))',
              }}
            >
              <style>{`
                .scroll-button {
                  transition: transform 0.2s ease, filter 0.2s ease;
                }
                .scroll-button:hover {
                  transform: rotate(-12deg) scale(1.1);
                  filter: drop-shadow(0 5px 10px rgba(212, 175, 55, 0.5));
                }
                .scroll-button .parchment-body {
                  transition: height 0.3s ease;
                }
                .scroll-button:hover .parchment-body {
                  height: 28px;
                }
                .scroll-button .bottom-spool {
                  transition: transform 0.3s ease;
                }
                .scroll-button:hover .bottom-spool {
                  transform: translateY(10px);
                }
                .scroll-button .text-lines {
                  transition: opacity 0.3s ease 0.1s;
                  opacity: 0;
                }
                .scroll-button:hover .text-lines {
                  opacity: 0.6;
                }
              `}</style>

              <svg width="36" height="46" viewBox="0 0 36 46" fill="none" style={{ overflow: 'visible' }}>
                <defs>
                  {/* Wood grain gradient */}
                  <linearGradient id="woodGrain" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8B4513" />
                    <stop offset="20%" stopColor="#A0522D" />
                    <stop offset="40%" stopColor="#8B4513" />
                    <stop offset="60%" stopColor="#A0522D" />
                    <stop offset="80%" stopColor="#8B4513" />
                    <stop offset="100%" stopColor="#6B3510" />
                  </linearGradient>
                  {/* Wood end cap */}
                  <radialGradient id="woodEnd">
                    <stop offset="0%" stopColor="#D2691E" />
                    <stop offset="60%" stopColor="#8B4513" />
                    <stop offset="100%" stopColor="#5D3A1A" />
                  </radialGradient>
                  {/* Parchment */}
                  <linearGradient id="parchmentFill" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#e8d4a8" />
                    <stop offset="10%" stopColor="#f4e4bc" />
                    <stop offset="90%" stopColor="#f4e4bc" />
                    <stop offset="100%" stopColor="#d4c094" />
                  </linearGradient>
                </defs>

                {/* Top wooden spool - drawn on top layer */}
                <g className="top-spool">
                  {/* Left end cap */}
                  <ellipse cx="4" cy="5" rx="3" ry="4" fill="url(#woodEnd)" />
                  {/* Spool body */}
                  <rect x="4" y="1" width="28" height="8" rx="1" fill="url(#woodGrain)" />
                  {/* Right end cap */}
                  <ellipse cx="32" cy="5" rx="3" ry="4" fill="url(#woodEnd)" />
                  {/* Wood grain lines */}
                  <line x1="10" y1="2" x2="10" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                  <line x1="18" y1="2" x2="18" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                  <line x1="26" y1="2" x2="26" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                </g>

                {/* Parchment paper - positioned between spools */}
                <rect className="parchment-body" x="7" y="9" width="22" height="18" rx="1" fill="url(#parchmentFill)" />

                {/* Text lines (revealed on unroll) */}
                <g className="text-lines">
                  <line x1="11" y1="14" x2="25" y2="14" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="11" y1="18" x2="23" y2="18" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="11" y1="22" x2="21" y2="22" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
                </g>

                {/* Bottom wooden spool */}
                <g className="bottom-spool">
                  {/* Left end cap */}
                  <ellipse cx="4" cy="31" rx="3" ry="4" fill="url(#woodEnd)" />
                  {/* Spool body */}
                  <rect x="4" y="27" width="28" height="8" rx="1" fill="url(#woodGrain)" />
                  {/* Right end cap */}
                  <ellipse cx="32" cy="31" rx="3" ry="4" fill="url(#woodEnd)" />
                  {/* Wood grain lines */}
                  <line x1="10" y1="28" x2="10" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                  <line x1="18" y1="28" x2="18" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                  <line x1="26" y1="28" x2="26" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                </g>
              </svg>
            </div>

            {/* Raptor Race Button - only for campaigns with enableRaptorDash setting */}
            {!!currentCampaign?.settings?.enableRaptorDash && (
            <button
              onClick={() => setShowDinoRace(true)}
              title="Launch Raptor Dash Mini-Game"
              style={{
                position: 'relative',
                padding: 'var(--spacing-xs) var(--spacing-md)',
                background: showDinoRace
                  ? 'linear-gradient(180deg, #228b22 0%, #145214 100%)'
                  : 'linear-gradient(180deg, #3d2817 0%, #2a1a0f 100%)',
                border: '2px solid #8b4513',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                letterSpacing: '0.05em',
                color: showDinoRace ? '#fff' : '#d4af37',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                transition: 'all 0.2s ease-out',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                🦖 Raptor Dash
              </span>
            </button>
            )}

            {/* GM Console Button - ornate golden toggle (DM only) */}
            {isGM && (
            <button
              data-tour="gm-console"
              onClick={toggleDMConsole}
              title="Toggle GM Console (`)"
              style={{
                position: 'relative',
                padding: 'var(--spacing-xs) var(--spacing-lg)',
                background: dmConsoleOpen
                  ? 'linear-gradient(180deg, #d4af37 0%, #a08030 50%, #8b6914 100%)'
                  : 'linear-gradient(180deg, #4a3728 0%, #2d1f15 100%)',
                border: '2px solid #d4af37',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: dmConsoleOpen ? '#1a1410' : '#d4af37',
                boxShadow: dmConsoleOpen
                  ? '0 0 12px rgba(212, 175, 55, 0.6), inset 0 1px 0 rgba(255,255,255,0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(212, 175, 55, 0.2)',
                transition: 'all 0.2s ease-out',
                textShadow: dmConsoleOpen
                  ? '0 1px 0 rgba(255,255,255,0.3)'
                  : '0 1px 2px rgba(0,0,0,0.5)',
              }}
            >
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)'
              }}>
                <span style={{ fontSize: '10px' }}>⚔</span>
                GM Console
                <span style={{ fontSize: '10px' }}>⚔</span>
              </span>
            </button>
            )}

            {/* Help / Tour button */}
            <button
              onClick={() => {
                const tourId = isGM ? 'gm-intro' : 'player-intro';
                useFeatureTourStore.getState().startTour(tourId);
              }}
              title="Feature Tour"
              style={{
                width: 28,
                height: 28,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: '50%',
                color: 'var(--color-text-secondary)',
                fontSize: '14px',
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              ?
            </button>

            <ConnectionIndicator />
            <PlayerSoundToggle />

            <ThemePicker />

            {/* Divider */}
            <div style={{
              width: '1px',
              height: '24px',
              backgroundColor: 'var(--color-border)',
            }} />

            {/* User info and logout */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
            }}>
              <button
                onClick={() => setShowProfile(true)}
                title="View Profile"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 'var(--spacing-xs)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {displayName}
              </button>
              <button
                onClick={signOut}
                title="Sign out"
                style={{
                  padding: 'var(--spacing-xs) var(--spacing-sm)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}>
          {/* Left Sidebar - Party & Combat */}
          <aside style={{
            width: '280px',
            backgroundColor: 'var(--color-surface)',
            borderRight: `2px solid var(--color-border)`,
            padding: 'var(--spacing-md)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-lg)',
            boxShadow: 'inset -10px 0 20px rgba(0,0,0,0.15)'
          }}>
            {/* Open Tasks — shows "Create Character" if player has no character */}
            <OpenTasksPanel />

            {/* Party panel */}
            <div className="ornate-panel" data-tour="party-panel" style={{ padding: 'var(--spacing-md)' }}>
              <PartyPanel />
            </div>

            {/*
              Combat furniture, only for systems that have combat.

              `adapter.combat` is optional, and its absence means the system has
              no tactical layer — not that ours is broken. Rendering an empty
              initiative tracker and a monster panel to a table playing a purely
              narrative game would be the app insisting on a subsystem the rules
              do not contain.
            */}
            {hasCombat && <InitiativePanel />}

            {/* Mob panel - DM only */}
            {hasCombat && isGM && (
            <div className="ornate-panel" style={{ padding: 'var(--spacing-md)' }}>
              <MobPanel />
            </div>
            )}

          </aside>

          {/* Center - Map Canvas */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Map or Mini-Game */}
            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {showDinoRace ? (
                <DinoRaceView roomCode={currentCampaignId || undefined} playerName={userName || undefined} onClose={() => setShowDinoRace(false)} />
              ) : (
                <>
                  {mapMode === 'iso' ? (
                    isLegacyIso() ? <IsoMapCanvas /> : (
                      <Suspense fallback={null}><ThreeMapCanvas /></Suspense>
                    )
                  ) : (
                    <MapCanvas />
                  )}
                  <VerticalToolbar onToolSelect={(tool) => console.log('Tool selected:', tool)} />
                </>
              )}
            </div>
          </div>

          {/* Right Sidebar - Chat */}
          <aside data-tour="chat-window" style={{
            width: '320px',
            backgroundColor: 'var(--color-surface)',
            borderLeft: `2px solid var(--color-border)`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'inset 10px 0 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              padding: 'var(--spacing-md)',
              borderBottom: `1px solid var(--color-accent)`,
              position: 'relative'
            }}>
              <SectionHeader title="Team Chat" style={{ marginBottom: 0 }} />
            </div>
            <ChatWindow playerName={userName || 'Player'} />
          </aside>

        </main>

        {/* GM Tray - unified DM console at bottom */}
        <Suspense fallback={null}>
          <GMTray />
        </Suspense>

        {/* Player Card Drawer - shows dealt cards */}
        <PlayerCardDrawer />

        {/* 3D Dice popup - opens on roll */}
        <Suspense fallback={null}>
          <DiceRoller onReady={setRollFn} />
        </Suspense>

        {/* Psyche thought overlay - player receives inner voice thoughts */}
        <PsycheThoughtDisplay />

        {/* NPC bark overlay - floating toasts for NPC speech */}
        <NpcBarkOverlay />

        {/* General-purpose notifications — surfaces silent failures
            (failed chat writes, dice not ready, etc.) so the user
            knows something needs attention. */}
        <NotificationOverlay />

        {/* Floating windows manager */}
        <WindowManager />

        {/* Profile modal */}
        {showProfile && <ProfilePage onClose={() => setShowProfile(false)} />}

        {/* Town Crier - shows on version updates or when manually opened */}
        <TownCrier forceShow={showTownCrier} onDismiss={() => setShowTownCrier(false)} />
        <UserNotice />

        {/* Feature Tour - onboarding walkthrough for new users */}
        <FeatureTour isGM={isGM} />

        {/* Floating bug reporter (renders only when logged in) */}
        <BugReportButton />
    </div>
  );
}

export default App;
